import React from 'react'
import * as Sentry from '@sentry/react'

import { createRoot } from 'react-dom/client'
import { Provider, ReactReduxContext } from 'react-redux'
import { HistoryRouter } from 'redux-first-history/rr6'

import './styles/globals.css'
import './i18n'
import App from './App'
import setupStore from './state/setupStore'

declare global {
    interface Window {
        __SERVER_DATA__: any
    }
}

Sentry.init({
    dsn: process.env.SENTRY_DSN || '',
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    environment: process.env.NODE_ENV,
    release: process.env.RELEASE || 'local',
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend: (event) => {
        if (process.env.NODE_ENV === 'development') {
            return null
        }
        return event
    },
})

const container = document.getElementById('root')!
const root = createRoot(container)

const { user, consoleAPIURL } = window.__SERVER_DATA__

// Injected here rather than imported by the store it is for: docs/REVIEW.md, Module
// System, holds why.
const middleware = import.meta.env.DEV
    ? [ (await import('redux-logger')).default ]
    : []

const { store, history } = setupStore({ user }, { consoleAPIURL, middleware })

if (process.env.NODE_ENV !== 'development') {
    delete window.__SERVER_DATA__
}

const initialize = (Component: React.ComponentType) => root.render(
    <Provider store={ store } context={ ReactReduxContext }>
        <HistoryRouter history={ history } context={ ReactReduxContext }>
            <Component />
        </HistoryRouter>
    </Provider>
)

initialize(App)

if (import.meta.hot) {
    import.meta.hot.accept('./App', (newModule) => {
        if (newModule) initialize(newModule.default)
    })
}
